import React, { useState, useRef, useEffect } from 'react';
import { resizeImage, calculateDimensions } from './resizerUtils';
import ImageUploader from '../../components/ImageUploader/ImageUploader';
import './Resizer.css';

const Resizer = () => {
  const [originalImage, setOriginalImage] = useState(null);
  const [resizedImage, setResizedImage] = useState(null);
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);
  const [isResizing, setIsResizing] = useState(false);
  const [originalDimensions, setOriginalDimensions] = useState({ width: 0, height: 0 });
  const [scaleMultiplier, setScaleMultiplier] = useState(1);
  const [useScale, setUseScale] = useState(false);
  const [activeTab, setActiveTab] = useState('dimensions'); // 'dimensions' or 'scale'
  const canvasRef = useRef(null);

  const handleImageSelected = (img) => {
    setOriginalImage(img);
    setOriginalDimensions({ width: img.width, height: img.height });
    setWidth(img.width);
    setHeight(img.height);
    setResizedImage(null);
  };

  useEffect(() => {
    if (useScale && originalDimensions.width && originalDimensions.height) {
      setWidth(Math.round(originalDimensions.width * scaleMultiplier));
      setHeight(Math.round(originalDimensions.height * scaleMultiplier));
    }
  }, [scaleMultiplier, useScale, originalDimensions]);

  const handleWidthChange = (e) => {
    const newWidth = e.target.value === '' ? '' : parseInt(e.target.value, 10);
    setWidth(newWidth);
    
    if (maintainAspectRatio && newWidth && originalDimensions.width) {
      const aspectRatio = originalDimensions.width / originalDimensions.height;
      setHeight(Math.round(newWidth / aspectRatio));
    }
  };

  const handleHeightChange = (e) => {
    const newHeight = e.target.value === '' ? '' : parseInt(e.target.value, 10);
    setHeight(newHeight);
    
    if (maintainAspectRatio && newHeight && originalDimensions.height) {
      const aspectRatio = originalDimensions.width / originalDimensions.height;
      setWidth(Math.round(newHeight * aspectRatio));
    }
  };

  const handleScaleChange = (e) => {
    const newScale = parseFloat(e.target.value);
    setScaleMultiplier(newScale);
  };

  const toggleUseScale = (e) => {
    const checked = e.target.checked;
    setUseScale(checked);
    
    if (checked && originalDimensions.width && originalDimensions.height) {
      setWidth(Math.round(originalDimensions.width * scaleMultiplier));
      setHeight(Math.round(originalDimensions.height * scaleMultiplier));
    }
  };

  const handleResize = async () => {
    if (!originalImage || !width || !height) return;
    
    setIsResizing(true);
    try {
      const result = await resizeImage(originalImage, width, height, canvasRef.current);
      setResizedImage(result);
    } catch (error) {
      console.error('Error resizing image:', error);
    } finally {
      setIsResizing(false);
    }
  };

  const handleDownload = () => {
    if (!resizedImage) return;
    
    const link = document.createElement('a');
    link.href = resizedImage;
    link.download = 'resized-image.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const resetToOriginal = () => {
    if (originalImage) {
      setWidth(originalDimensions.width);
      setHeight(originalDimensions.height);
      setScaleMultiplier(1);
      setUseScale(false);
    }
  };

  // Predefined scale options
  const handleQuickScale = (factor) => {
    setScaleMultiplier(factor);
    setUseScale(true);
    setActiveTab('scale');
  };

  const switchToScaleMode = () => {
    setActiveTab('scale');
    setUseScale(true);
  };

  const switchToDimensionsMode = () => {
    setActiveTab('dimensions');
    setUseScale(false);
  };

  return (
    <div className="resizer tool-container">
      <div className="container">
        <div className="tool-header">
          <h1>Image Resizer</h1>
          <p>Resize your images to specific dimensions</p>
        </div>
        
        <div className="tool-content">
          <div className="uploader-section">
            <ImageUploader 
              onImageSelected={handleImageSelected}
              selectedImage={originalImage}
            />
          </div>
          
          {originalImage && (
            <div className="resize-workflow">
              <div className="resize-controls-card">
                <div className="resize-mode-tabs">
                  <button 
                    className={`mode-tab ${activeTab === 'dimensions' ? 'active' : ''}`}
                    onClick={switchToDimensionsMode}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="3" y1="9" x2="21" y2="9"></line>
                      <line x1="9" y1="21" x2="9" y2="9"></line>
                    </svg>
                    <span>Dimensions</span>
                  </button>
                  <button 
                    className={`mode-tab ${activeTab === 'scale' ? 'active' : ''}`}
                    onClick={switchToScaleMode}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 3L9 15"></path>
                      <path d="M12 3H21V12"></path>
                      <path d="M3 21L15 9"></path>
                      <path d="M3 12V21H12"></path>
                    </svg>
                    <span>Scale</span>
                  </button>
                </div>
                
                <div className="resize-controls-content">
                  {activeTab === 'dimensions' ? (
                    <div className="dimensions-panel">
                      <div className="dimensions-inputs">
                        <div className="control-group">
                          <label htmlFor="width">Width (px)</label>
                          <div className="input-with-icon">
                            <input 
                              type="number" 
                              id="width" 
                              value={width} 
                              onChange={handleWidthChange} 
                              min="1"
                              className="dimension-input"
                            />
                            <span className="input-unit">px</span>
                          </div>
                        </div>
                        
                        <div className="dimension-separator">×</div>
                        
                        <div className="control-group">
                          <label htmlFor="height">Height (px)</label>
                          <div className="input-with-icon">
                            <input 
                              type="number" 
                              id="height" 
                              value={height} 
                              onChange={handleHeightChange} 
                              min="1"
                              className="dimension-input"
                            />
                            <span className="input-unit">px</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="control-group checkbox-group">
                        <input 
                          type="checkbox" 
                          id="maintain-ratio" 
                          checked={maintainAspectRatio} 
                          onChange={(e) => setMaintainAspectRatio(e.target.checked)} 
                        />
                        <label htmlFor="maintain-ratio">Maintain aspect ratio</label>
                      </div>
                    </div>
                  ) : (
                    <div className="scale-panel">
                      <div className="scale-info">
                        <div className="original-dimensions-info">
                          Original: {originalDimensions.width} × {originalDimensions.height}px
                        </div>
                        <div className="scaled-dimensions-info">
                          Output: {width} × {height}px
                        </div>
                      </div>
                      
                      <div className="scale-value-container">
                        <div className="scale-value">{scaleMultiplier}×</div>
                      </div>
                      
                      <div className="scale-slider-container">
                        <input
                          type="range"
                          min="0.1"
                          max="10"
                          step="0.1"
                          value={scaleMultiplier}
                          onChange={handleScaleChange}
                          className="scale-slider"
                        />
                      </div>
                      
                      <div className="quick-scale-buttons">
                        <button className="scale-btn" onClick={() => handleQuickScale(0.5)}>0.5×</button>
                        <button className="scale-btn" onClick={() => handleQuickScale(1)}>1×</button>
                        <button className="scale-btn" onClick={() => handleQuickScale(2)}>2×</button>
                        <button className="scale-btn" onClick={() => handleQuickScale(3)}>3×</button>
                        <button className="scale-btn" onClick={() => handleQuickScale(5)}>5×</button>
                        <button className="scale-btn" onClick={() => handleQuickScale(10)}>10×</button>
                      </div>
                    </div>
                  )}
                  
                  <div className="action-buttons">
                    <button 
                      className="btn btn-secondary" 
                      onClick={resetToOriginal}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                        <path d="M3 3v5h5"></path>
                      </svg>
                      Reset
                    </button>
                    
                    <button 
                      className="btn btn-primary resize-btn" 
                      onClick={handleResize}
                      disabled={isResizing || !width || !height}
                    >
                      {isResizing ? (
                        <>
                          <svg className="spinner" viewBox="0 0 50 50">
                            <circle cx="25" cy="25" r="20"></circle>
                          </svg>
                          Resizing...
                        </>
                      ) : (
                        <>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <circle cx="8.5" cy="8.5" r="1.5"></circle>
                            <polyline points="21 15 16 10 5 21"></polyline>
                          </svg>
                          Resize Image
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="image-preview-section">
                <div className="image-preview-container">
                  <div className="preview-header">
                    <h3>Original</h3>
                    <div className="preview-dimensions">{originalDimensions.width} × {originalDimensions.height}px</div>
                  </div>
                  <div className="image-preview">
                    <img src={originalImage.src} alt="Original" />
                  </div>
                </div>
                
                <div className="preview-arrow">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14"></path>
                    <path d="m12 5 7 7-7 7"></path>
                  </svg>
                </div>
                
                <div className="image-preview-container">
                  <div className="preview-header">
                    <h3>Output</h3>
                    {resizedImage && <div className="preview-dimensions">{width} × {height}px</div>}
                  </div>
                  <div className="image-preview">
                    {resizedImage ? (
                      <img src={resizedImage} alt="Resized" />
                    ) : (
                      <div className="placeholder-container">
                        <div className="resize-placeholder">
                          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <circle cx="8.5" cy="8.5" r="1.5"></circle>
                            <polyline points="21 15 16 10 5 21"></polyline>
                          </svg>
                          <p>Click 'Resize Image' to see the result here</p>
                        </div>
                      </div>
                    )}
                  </div>
                  {resizedImage && (
                    <button className="btn btn-primary download-btn" onClick={handleDownload}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                      Download
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Hidden canvas for image processing */}
          <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
        </div>
      </div>
    </div>
  );
};

export default Resizer; 